<!--
    "This phone already exists" - merge or keep a separate card.

    Two entrances, one modal:

    ask()           the user is sitting in front of a create form and typed a phone we already
                    know. Nothing is saved yet, so it just answers 'merge' / 'new' / 'cancel' and
                    the caller decides what to save (see NewCustomerPage.save).

    showForResolve() a lead arrived from outside (contact form / mit4mit / Facebook) with a known
                    phone. Nobody was there to answer, so the lead was saved as its own card and a
                    bell notification links here - this mode merges the two existing cards (or
                    leaves them alone).
-->
<template>
    <Modal ref="modal"
           :large="true"
           title="מספר טלפון קיים במערכת">

        <template v-slot:body>

            <div v-if="loading"
                 style="text-align:center;padding:30px;color:#777">
                טוען...
            </div>

            <div v-else-if="alreadyHandled"
                 style="padding:10px 0">
                הכפילות כבר טופלה - הפנייה מוזגה או נמחקה בינתיים.
            </div>

            <div v-else>

                <div style="margin-bottom:20px">
                    מספר הטלפון
                    <span style="direction:ltr;display:inline-block;font-weight:600">{{ phone }}</span>
                    כבר קיים במערכת.
                </div>

                <!-- the inquiry that just arrived (resolve mode only) -->
                <div v-if="newCustomer"
                     style="margin-bottom:20px">

                    <h6 style="color:#777">הפנייה החדשה שהתקבלה</h6>

                    <div class="duplicatePhoneCard">

                        <div style="font-weight:600">
                            {{ contactNames(newCustomer) }}
                        </div>

                        <div style="font-size:13px;color:#777">
                            {{ contactDetails(newCustomer) }}
                        </div>

                        <div v-for="event in newCustomer.events"
                             :key="event.id"
                             style="font-size:13px;color:#777;margin-top:4px">
                            {{ eventLine(event) }}
                        </div>

                    </div>
                </div>

                <h6 style="color:#777">
                    {{ duplicates.length > 1 ? 'הכרטיסים הקיימים' : 'הכרטיס הקיים' }}
                </h6>

                <div v-for="duplicate in duplicates"
                     :key="duplicate.id"
                     class="duplicatePhoneCard"
                     :class="{ chosen: selectedCustomerId == duplicate.id, selectable: duplicates.length > 1 }"
                     @click="selectedCustomerId = duplicate.id">

                    <div style="display:flex;align-items:center;gap:8px">

                        <span v-if="duplicates.length > 1"
                              class="planup-checkbox"
                              :class="{ checked: selectedCustomerId == duplicate.id }"
                              style="position:relative;top:1px">
                            <i class="fa fa-check"></i>
                        </span>

                        <span style="font-weight:600">
                            {{ contactNames(duplicate) }}
                        </span>

                        <span v-if="duplicate.archived"
                              style="background:#fff3cd;color:#856404;border-radius:10px;padding:1px 8px;font-size:12px">
                            בארכיון
                        </span>

                        <a :href="`/customer/${duplicate.id}`"
                           target="_blank"
                           @click.stop
                           style="margin-right:auto;font-size:13px;color:#ab8ce4">
                            פתח בלשונית חדשה
                        </a>

                    </div>

                    <div style="font-size:13px;color:#777">
                        {{ contactDetails(duplicate) }}
                    </div>

                    <div v-if="!duplicate.events.length"
                         style="font-size:13px;color:#777;margin-top:4px">
                        אין אירועים בכרטיס
                    </div>

                    <div v-for="event in duplicate.events"
                         :key="event.id"
                         style="font-size:13px;color:#777;margin-top:4px">
                        {{ eventLine(event) }}
                    </div>

                </div>

                <div style="margin-top:15px;font-size:13px;color:#777">
                    מיזוג מעביר את הפנייה החדשה, אנשי הקשר והפרטים אל הכרטיס הקיים. פרטים שכבר קיימים
                    בכרטיס לא ידרסו.
                </div>

            </div>

        </template>

        <template v-slot:buttons>

            <button v-if="alreadyHandled"
                    type="button"
                    class="btn btn-primary"
                    data-bs-dismiss="modal">
                סגור
            </button>

            <template v-else-if="!loading">

                <button type="button"
                        class="btn btn-primary"
                        :disabled="!selectedCustomerId || working"
                        @click="chooseMerge">
                    מזג לכרטיס הקיים
                </button>

                <button type="button"
                        class="btn btn-secondary"
                        :disabled="working"
                        @click="chooseSeparate">
                    {{ mode == 'resolve' ? 'השאר בנפרד' : 'צור לקוח חדש' }}
                </button>

                <button v-if="mode == 'ask'"
                        type="button"
                        class="btn btn-light"
                        :disabled="working"
                        @click="cancel">
                    ביטול
                </button>

            </template>

        </template>

    </Modal>
</template>

<script>

export default {
    name: 'DuplicatePhoneModal',

    data() {
        return {
            mode: 'ask',                //'ask' (create form) | 'resolve' (bell notification)
            phone: '',
            duplicates: [],
            newCustomer: null,          //resolve mode: the card the incoming lead was saved as
            selectedCustomerId: null,
            loading: false,
            working: false,
            alreadyHandled: false,
            resolvePromise: null,
        }
    },

    mounted() {

        //the X, Esc and the backdrop all close the modal without touching a button - an unanswered
        //modal must still settle the promise (as a cancel) or the caller's save() hangs forever
        this.$refs.modal.$el.addEventListener('hidden.bs.modal', () => this.settle({ choice: 'cancel' }));
    },

    methods: {

        // ── entrances ───────────────────────────────────────────────────────────────────────

        // Create form: nothing is saved yet. Resolves { choice: 'merge'|'new'|'cancel', customerId }
        ask(phone, duplicates) {

            this.mode = 'ask';
            this.phone = phone;
            this.duplicates = duplicates || [];
            this.newCustomer = null;
            this.alreadyHandled = false;
            this.loading = false;
            this.working = false;
            this.selectedCustomerId = this.duplicates.length ? this.duplicates[0].id : null;

            this.$refs.modal.show();

            return new Promise(resolve => { this.resolvePromise = resolve });
        },

        // Bell notification: both cards already exist on the server. (The notification itself is
        // marked seen by the bell/notifications page the moment it is clicked - not here.)
        showForResolve(newCustomerId, existingCustomerId) {

            this.mode = 'resolve';
            this.phone = '';
            this.duplicates = [];
            this.newCustomer = null;
            this.alreadyHandled = false;
            this.working = false;
            this.selectedCustomerId = null;
            this.loading = true;

            this.$refs.modal.show();

            this.$handlers.customerHandler.getDuplicatePair(newCustomerId, existingCustomerId).then(pair => {

                this.loading = false;

                //whoever got to the notification first already merged (or deleted) one of the two
                if (!pair || !pair.newCustomer || !pair.existingCustomer) {
                    this.alreadyHandled = true;
                    return;
                }

                this.newCustomer = pair.newCustomer;
                this.duplicates = [pair.existingCustomer];
                this.selectedCustomerId = pair.existingCustomer.id;

                let matched = pair.newCustomer.contacts.find(contact => contact.phone);
                this.phone = matched ? matched.phone : '';

            }).catch(() => {
                this.loading = false;
                this.alreadyHandled = true;
            });

            return new Promise(resolve => { this.resolvePromise = resolve });
        },

        // ── answers ─────────────────────────────────────────────────────────────────────────

        chooseMerge() {

            if (!this.selectedCustomerId)
                return;

            //ask mode hands the decision back - the caller owns the save
            if (this.mode == 'ask') {
                this.finish({ choice: 'merge', customerId: this.selectedCustomerId });
                return;
            }

            //resolve mode does the merge itself: both cards already exist
            this.working = true;

            this.$handlers.customerHandler.mergeCustomers(this.newCustomer.id, this.selectedCustomerId).then(() => {

                this.working = false;
                this.finish({ choice: 'merge', customerId: this.selectedCustomerId });

                this.AlertSuccess('הפנייה מוזגה לכרטיס הקיים');
                this.$router.push(`/customer/${this.selectedCustomerId}`);

            }).catch(() => {
                this.working = false;
                this.AlertError('המיזוג נכשל');
            });
        },

        chooseSeparate() {
            this.finish({ choice: 'new' });
        },

        cancel() {
            this.finish({ choice: 'cancel' });
        },

        // The answer is known the moment a button is pressed, so report it right away rather than
        // waiting for the fade-out - and then make sure the modal really closes.
        finish(answer) {
            this.settle(answer);
            this.hideModal();
        },

        hideModal() {

            this.$refs.modal.hide();

            // bootstrap's hide() is a silent no-op while the fade-IN is still transitioning, which
            // is exactly what a click landing within ~300ms of the modal opening hits - the modal
            // would then stay on screen forever. Try again once the transition can't be running.
            setTimeout(() => {
                if (this.$refs.modal && this.$refs.modal.$el.classList.contains('show'))
                    this.$refs.modal.hide();
            }, 400);
        },

        settle(answer) {

            if (!this.resolvePromise)
                return;

            let resolve = this.resolvePromise;
            this.resolvePromise = null;
            resolve(answer);
        },

        // ── display ─────────────────────────────────────────────────────────────────────────

        contactNames(customer) {

            let names = (customer.contacts || [])
                .map(contact => `${contact.firstName || ''} ${contact.lastName || ''}`.trim())
                .filter(name => name);

            return names.length ? names.join(' ו') : 'ללא שם';
        },

        contactDetails(customer) {

            let details = [];

            (customer.contacts || []).forEach(contact => {
                if (contact.phone && details.indexOf(contact.phone) == -1)
                    details.push(contact.phone);
            });

            (customer.contacts || []).forEach(contact => {
                if (contact.email && details.indexOf(contact.email) == -1)
                    details.push(contact.email);
            });

            return details.join(' · ');
        },

        eventLine(event) {

            let parts = [event.name || 'פנייה ללא שם'];

            if (event.planupEventStatus)
                parts.push(event.planupEventStatus);

            if (event.date)
                parts.push(this.$globals.niceDate(event.date));
            else if (event.created)
                parts.push(`נפתח ב-${this.$globals.niceDate(event.created)}`);

            if (event.archived)
                parts.push('בארכיון');

            return parts.join(' · ');
        },

    },
}
</script>

<style scoped>
.duplicatePhoneCard {
    border: solid 1px #eeeeee;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 10px;
}

.duplicatePhoneCard.selectable {
    cursor: pointer;
}

.duplicatePhoneCard.chosen {
    border-color: #ab8ce4;
    background: #faf7ff;
}
</style>
